import React, { useState, useMemo, useCallback, useEffect } from "react";
import { DDNav } from "../../../components/Admin/DDNav/DDNav";
import { DDLoadError } from "../../../components/Admin/DDLoadError";
import { ConfirmModal } from "../../../components/Admin/ConfirmModal/ConfirmModal";
import { useAuth } from "../../../hooks";
import { DdPolicyTracker } from "../../../api";
import {
  MASTER_POLICIES, STATUS_LABELS, STATUS_OPTIONS, CAT_OPTIONS, STD_OPTIONS,
  policyRequirements, trBuildDefault, trMergeWithDefaults, trCalcNextReview, trDaysUntil, trEffectiveStatus,
} from "./policyTrackerCatalog";
import "./PolicyTracker.scss";

const ddTrApi = new DdPolicyTracker();

export function PolicyTracker() {
  const { accessToken } = useAuth();
  const [data, setData]     = useState(trBuildDefault);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [open, setOpen]     = useState({});
  const [fStatus, setFStatus] = useState("");
  const [fCat, setFCat]     = useState("");
  const [fStd, setFStd]     = useState("");
  const [fQ, setFQ]         = useState("");
  const [uploading, setUploading] = useState({});
  const [confirm, setConfirm] = useState({ open: false, msg: "", onConfirm: null });

  // Si la carga falla no se muestra (ni se guarda) el tracker vacío.
  useEffect(() => {
    if (!accessToken) return;
    setLoading(true);
    setLoadError(false);
    ddTrApi.getData(accessToken)
      .then(d => { if (d) setData(trMergeWithDefaults(d)); })
      .catch((err) => { console.error("Error cargando Policy Tracker", err); setLoadError(true); })
      .finally(() => setLoading(false));
  }, [accessToken, reloadKey]);

  const persist = useCallback((next) => {
    setData({ ...next });
    if (accessToken) ddTrApi.saveData(accessToken, next).catch(() => {});
  }, [accessToken]);

  const update = (code, field, val) => {
    const next = { ...data, [code]: { ...data[code], [field]: val } };
    persist(next);
  };

  const updateLang = (code, lang, checked) => {
    const next = { ...data, [code]: { ...data[code], languages: { ...data[code].languages, [lang]: checked } } };
    persist(next);
  };

  const toggleCard = (code) => setOpen(prev => ({ ...prev, [code]: !prev[code] }));

  const handleUploadFile = async (code, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert("Archivo demasiado grande (máx 10 MB).");
      return;
    }
    setUploading(prev => ({ ...prev, [code]: true }));
    try {
      const formData = new FormData();
      formData.append("file", file);
      const result = await ddTrApi.uploadFile(accessToken, formData);
      update(code, "file", result.file);
    } catch (err) {
      alert(err?.msg || "Error al subir el archivo.");
    } finally {
      setUploading(prev => ({ ...prev, [code]: false }));
    }
  };

  const handleDownloadFile = async (code) => {
    const file = data[code]?.file;
    if (!file) return;
    const url = await ddTrApi.getFile(accessToken, file.key);
    if (!url) { alert("No se pudo descargar el archivo."); return; }
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleRemoveFile = (code) => {
    const file = data[code]?.file;
    if (!file) return;
    setConfirm({
      open: true,
      msg: `¿Eliminar el archivo cargado para ${code}? Esta acción no se puede deshacer.`,
      onConfirm: async () => {
        try { await ddTrApi.deleteFile(accessToken, file.key); } catch { /* orphaned file on disk, non-fatal */ }
        update(code, "file", null);
        setConfirm({ open: false, msg: "", onConfirm: null });
      },
    });
  };

  // ── KPIs ────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    let vigente = 0, borrador = 0, vencida = 0, no_tiene = 0, en_revision = 0;
    let upcoming = 0;
    let totalReqs = 0, coveredReqs = 0;
    MASTER_POLICIES.forEach(p => {
      const st = trEffectiveStatus(p.code, data);
      if (st === "vigente") vigente++;
      else if (st === "borrador") borrador++;
      else if (st === "vencida") vencida++;
      else if (st === "no_tiene") no_tiene++;
      else if (st === "en_revision") en_revision++;
      const days = trDaysUntil(p.code, data);
      if (days !== null && days >= 0 && days <= 60) upcoming++;
      const reqs = policyRequirements(p.code);
      totalReqs += reqs.length;
      if (st === "vigente" || st === "en_revision") coveredReqs += reqs.length;
    });
    const stdCov = totalReqs ? Math.round(coveredReqs / totalReqs * 100) : 0;
    return { vigente, borrador, vencida, no_tiene, en_revision, upcoming, stdCov, coveredReqs, totalReqs };
  }, [data]);

  // ── Filtered list ────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return MASTER_POLICIES.filter(p => {
      const st = trEffectiveStatus(p.code, data);
      if (fStatus && st !== fStatus) return false;
      if (fCat && p.cat !== fCat) return false;
      if (fStd) {
        const reqs = policyRequirements(p.code);
        if (!reqs.some(r => r.stdCode === fStd)) return false;
      }
      if (fQ) {
        const q = fQ.toLowerCase();
        if (!((p.code + " " + p.title + " " + (p.sub || "")).toLowerCase().includes(q))) return false;
      }
      return true;
    });
  }, [data, fStatus, fCat, fStd, fQ]);

  const vigCls = stats.vigente >= MASTER_POLICIES.length * 0.7 ? "good" : stats.vigente >= MASTER_POLICIES.length * 0.4 ? "warn" : "crit";
  const gapCls = (stats.vencida + stats.no_tiene) === 0 ? "good" : (stats.vencida + stats.no_tiene) > 5 ? "crit" : "warn";
  const upCls  = stats.upcoming > 0 ? "warn" : "good";
  const covCls = stats.stdCov >= 80 ? "good" : stats.stdCov >= 50 ? "warn" : "crit";

  if (loading) return <div className="pt-page"><DDNav /><div style={{ padding: "40px 22px", color: "#6B7280" }}>Cargando…</div></div>;
  if (loadError) return <DDLoadError className="pt-page" onRetry={() => setReloadKey((k) => k + 1)} />;

  return (
    <div className="pt-page">
      <DDNav />

      <div className="pt-ph">
        <div>
          <h1>Policy Lifecycle Tracker</h1>
          <div className="pt-ph-sub">Las políticas no son archivos · son organismos vivos · estado, vencimientos, archivos firmados y mapeo a estándares en un solo lugar</div>
        </div>
      </div>

      {/* Intro */}
      <div className="pt-intro">
        <h3>📊 Cómo usar el Tracker</h3>
        <p>Para cada una de las <b>20 políticas</b> que los estándares de auditoría exigen, registrá: <b>estado actual</b>, <b>fecha de última revisión firmada</b>, <b>firmante</b>, <b>idiomas disponibles</b>, <b>% de personal capacitado</b> y <b>notas de gaps</b>.</p>
        <p>El sistema calcula automáticamente: <b>próxima revisión</b> (24 meses desde la última), <b>alertas de vencimiento</b> (60 días antes y al vencer) y <b>cobertura ante estándares</b> de auditoría.</p>
      </div>

      {/* KPIs */}
      <div className="pt-stats">
        <div className={`pt-stat pt-stat--${vigCls}`}>
          <div className="pt-stat-lbl">Políticas vigentes</div>
          <div className="pt-stat-val">{stats.vigente} / {MASTER_POLICIES.length}</div>
          <div className="pt-stat-sub">{stats.borrador} borrador · {stats.en_revision} en revisión</div>
        </div>
        <div className={`pt-stat pt-stat--${gapCls}`}>
          <div className="pt-stat-lbl">Gap crítico</div>
          <div className="pt-stat-val">{stats.vencida + stats.no_tiene}</div>
          <div className="pt-stat-sub">{stats.vencida} vencidas + {stats.no_tiene} no existen</div>
        </div>
        <div className={`pt-stat pt-stat--${upCls}`}>
          <div className="pt-stat-lbl">Vencen en 60 días</div>
          <div className="pt-stat-val">{stats.upcoming}</div>
          <div className="pt-stat-sub">Programar revisión y refirma</div>
        </div>
        <div className={`pt-stat pt-stat--${covCls}`}>
          <div className="pt-stat-lbl">Cobertura de estándares</div>
          <div className="pt-stat-val">{stats.stdCov}%</div>
          <div className="pt-stat-sub">{stats.coveredReqs} de {stats.totalReqs} requerimientos cubiertos</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="pt-filters">
        <span className="pt-f-lbl">Filtrar:</span>
        <select value={fStatus} onChange={e => setFStatus(e.target.value)}>
          <option value="">Todos los estados</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
        <select value={fCat} onChange={e => setFCat(e.target.value)}>
          <option value="">Todas las categorías</option>
          {CAT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={fStd} onChange={e => setFStd(e.target.value)}>
          <option value="">Todos los estándares</option>
          {STD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <input type="text" placeholder="Buscar..." value={fQ} onChange={e => setFQ(e.target.value)} />
        <span className="pt-f-count">{filtered.length} de {MASTER_POLICIES.length}</span>
      </div>

      {/* Lista */}
      <div className="pt-list">
        {filtered.length === 0 && (
          <div className="pt-empty">📭 No hay políticas que coincidan con los filtros</div>
        )}
        {filtered.map(p => {
          const d    = data[p.code];
          const st   = trEffectiveStatus(p.code, data);
          const days = trDaysUntil(p.code, data);
          const next = trCalcNextReview(d.lastReview);
          const reqs = policyRequirements(p.code);
          const isOpen = open[p.code];

          let deadlineText = "";
          let deadlineCls  = "";
          if (d.lastReview && next) {
            if (days < 0)        { deadlineCls = "crit"; deadlineText = `⚠ vencida hace ${Math.abs(days)} días`; }
            else if (days <= 60) { deadlineCls = "warn"; deadlineText = `⏰ vence en ${days} días (${next})`; }
            else                 { deadlineText = `próx. ${next}`; }
          } else if (st === "no_tiene") {
            deadlineCls = "crit"; deadlineText = "— sin documento";
          }

          return (
            <div key={p.code} className={`pt-card pt-card--${st}${isOpen ? " pt-card--open" : ""}`}>
              <div className="pt-card-hd" onClick={() => toggleCard(p.code)}>
                <span className="pt-code">{p.code}</span>
                <div className="pt-title">
                  {p.title}
                  <small>{p.sub}</small>
                </div>
                {deadlineText && <span className={`pt-deadline${deadlineCls ? " pt-deadline--" + deadlineCls : ""}`}>{deadlineText}</span>}
                <span className={`pt-status pt-status--${st}`}>{STATUS_LABELS[st]}</span>
                <span className="pt-arrow">{isOpen ? "▲" : "▼"}</span>
              </div>

              {isOpen && (
                <div className="pt-card-body">
                  {/* Estándares */}
                  <div className="pt-stds-box">
                    <div className="pt-stds-h">📋 Estándares que requieren esta política</div>
                    {reqs.length > 0 ? (
                      <>
                        <div className="pt-stds-row">
                          {reqs.map((r, i) => (
                            <span key={i} className="pt-std-badge" title={`${r.secN} · ${r.secTitle}`}>
                              {r.stdCode} {r.secN}
                            </span>
                          ))}
                        </div>
                        <div className="pt-stds-note">{reqs.length} requerimiento(s) de auditoría</div>
                      </>
                    ) : (
                      <div className="pt-stds-none">No mapeada a estándares · política voluntaria</div>
                    )}
                    {p.note && <div className="pt-policy-note"><b>Nota:</b> {p.note}</div>}
                  </div>

                  {/* Formulario */}
                  <div className="pt-form-grid">
                    <div className="pt-fld">
                      <label>Estado de la política</label>
                      <select value={d.status} onChange={e => update(p.code, "status", e.target.value)}>
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                      </select>
                    </div>

                    <div className="pt-fld">
                      <label>Última revisión firmada</label>
                      <input type="date" value={d.lastReview} onChange={e => update(p.code, "lastReview", e.target.value)} />
                      {d.lastReview && <div className="pt-hint">Próxima revisión: <b>{next}</b> (24 meses)</div>}
                    </div>

                    <div className="pt-fld">
                      <label>Firmante / Aprobador</label>
                      <input type="text" value={d.approvedBy} placeholder="Nombre y cargo" onChange={e => update(p.code, "approvedBy", e.target.value)} />
                    </div>

                    <div className="pt-fld">
                      <label>% de personal capacitado</label>
                      <input type="number" min="0" max="100" value={d.trainingPct} onChange={e => update(p.code, "trainingPct", Number(e.target.value))} />
                      <div className="pt-hint">Cobertura de capacitación documentada</div>
                    </div>

                    <div className="pt-fld pt-fld--full">
                      <label>Idiomas disponibles</label>
                      <div className="pt-chk-row">
                        <label className="pt-chk"><input type="checkbox" checked={d.languages?.sp || false} onChange={e => updateLang(p.code, "sp", e.target.checked)} /> 🇪🇸 Español</label>
                        <label className="pt-chk"><input type="checkbox" checked={d.languages?.pt || false} onChange={e => updateLang(p.code, "pt", e.target.checked)} /> 🇧🇷 Português</label>
                        <label className="pt-chk"><input type="checkbox" checked={d.languages?.en || false} onChange={e => updateLang(p.code, "en", e.target.checked)} /> 🇺🇸 English</label>
                      </div>
                    </div>

                    <div className="pt-fld pt-fld--full">
                      <label>Archivo de la política firmada</label>
                      <div className="pt-file-box">
                        {d.file && d.file.name ? (
                          <>
                            <div className="pt-file-info">
                              <b>📄 {d.file.name}</b><br />
                              <span>{Math.round((d.file.size || 0) / 1024)} KB · cargado {d.file.uploadedAt || "—"}</span>
                            </div>
                            <button type="button" className="pt-file-btn pt-file-btn--primary" onClick={() => handleDownloadFile(p.code)}>📥 Descargar</button>                            <button type="button" className="pt-file-btn pt-file-btn--del" onClick={() => handleRemoveFile(p.code)}>✕ Eliminar</button>
                            <label className="pt-file-btn">
                              {uploading[p.code] ? "Subiendo…" : "📤 Reemplazar"}
                              <input type="file" accept=".pdf,.docx,.doc" style={{ display: "none" }} disabled={!!uploading[p.code]} onChange={e => handleUploadFile(p.code, e)} />
                            </label>
                          </>
                        ) : (
                          <>
                            <div className="pt-file-info"><span className="pt-no-file">Sin archivo cargado · subí el .pdf o .docx firmado para tenerlo trazable</span></div>
                            <label className="pt-file-btn pt-file-btn--primary">
                              {uploading[p.code] ? "Subiendo…" : "📤 Subir archivo"}
                              <input type="file" accept=".pdf,.docx,.doc" style={{ display: "none" }} disabled={!!uploading[p.code]} onChange={e => handleUploadFile(p.code, e)} />
                            </label>
                          </>
                        )}
                      </div>
                      <div className="pt-hint">Formatos aceptados: .pdf, .docx, .doc</div>
                    </div>

                    <div className="pt-fld pt-fld--full">
                      <label>Notas · gaps detectados · elementos faltantes</label>
                      <textarea
                        value={d.notes}
                        placeholder="Ej: falta sección sobre subcontratación, falta tabla de marco normativo actualizado, falta firma de RRHH..."
                        onChange={e => update(p.code, "notes", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="pt-actions">
                    {p.inRepo
                      ? <span className="pt-repo-avail">📥 Plantilla disponible en el Repositorio · descargala desde allí</span>
                      : <span className="pt-repo-missing">📋 Plantilla todavía no generada en el Repositorio</span>
                    }
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ConfirmModal
        open={confirm.open}
        message={confirm.msg}
        danger
        onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm({ open: false, msg: "", onConfirm: null })}
      />
    </div>
  );
}
